import { useState, useEffect } from 'react';
import api, { getErrorMessage } from '../utils/api';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CalendarBlank, Clock, Plus, Lightning } from '@phosphor-icons/react';

const Batches = () => {
  const [batches, setBatches] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { activeBranch } = useBranch();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '09:40'
  });

  useEffect(() => {
    if (activeBranch) {
      fetchBatches();
    }
  }, [selectedDate, activeBranch]);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/batches`, {
        params: { 
          date: selectedDate,
          branch_id: activeBranch?.branch_id
        }
      });
      setBatches(response.data);
    } catch (error) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBatch = async (e) => {
    e.preventDefault();
    try {
      await api.post('/batches', {
        ...formData,
        branch_id: activeBranch?.branch_id
      });
      toast.success('Batch created successfully');
      setShowAddDialog(false);
      fetchBatches();
    } catch (error) {
      toast.error('Failed to create batch');
    }
  };

  const handleGenerateBatches = async () => {
    setGenerating(true);
    try {
      const response = await api.post(`/batches/generate-daily`, null, {
        params: { 
          date: selectedDate,
          branch_id: activeBranch?.branch_id
        }
      });
      
      if (response.data.batches_created > 0) {
        if (response.data.batches_existing > 0) {
          toast.success(`✓ Created ${response.data.batches_created} missing batches. ${response.data.batches_existing} already existed.`);
        } else {
          toast.success(`✓ Generated all 16 batches successfully!`);
        }
      } else if (response.data.batches_existing > 0) {
        toast.info('All 16 batches already exist for this date');
      } else if (response.data.batches) {
        toast.success(`Generated ${response.data.batches} batches successfully!`);
      } else {
        toast.info(response.data.message);
      }
      
      fetchBatches();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate batches'));
    } finally {
      setGenerating(false);
    }
  };

  const getTimeOfDay = (time) => {
    const hour = parseInt(time.split(':')[0]);
    if (hour < 12) return 'Morning';
    if (hour < 17) return 'Afternoon';
    return 'Evening';
  };

  const groupBatchesByTimeOfDay = () => {
    const grouped = {
      'Morning': [],
      'Afternoon': [],
      'Evening': []
    };
    
    batches.forEach(batch => {
      const timeOfDay = getTimeOfDay(batch.start_time);
      grouped[timeOfDay].push(batch);
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  const groupedBatches = groupBatchesByTimeOfDay();

  return (
    <Layout>
      <div data-testid="batches-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">Batch Schedule</h1>
            <p className="text-sm sm:text-base text-[#1C1C1A]/60">16 batches daily from 7:40 AM • 40 minutes each • 15 beds per batch</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="generate-batches-button"
              onClick={handleGenerateBatches}
              disabled={generating}
              className="bg-[#C05C42] hover:bg-[#C05C42]/90 text-white rounded-md flex items-center gap-2"
            >
              <Lightning size={20} weight="fill" />
              {generating ? 'Generating...' : 'Generate 16 Batches'}
            </Button>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-batch-button" className="bg-[#2C4C3B] hover:bg-[#2C4C3B]/90 text-white rounded-md flex items-center gap-2">
                  <Plus size={20} />
                  Add Single Batch
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-semibold tracking-tight text-[#2C4C3B]">Create New Batch</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddBatch} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Date</Label>
                    <Input
                      data-testid="batch-date-input"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Start Time</Label>
                      <Input
                        data-testid="batch-start-input"
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">End Time</Label>
                      <Input
                        data-testid="batch-end-input"
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <Button data-testid="submit-batch-button" type="submit" className="w-full bg-[#2C4C3B] hover:bg-[#2C4C3B]/90 text-white rounded-md">
                    Create Batch
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A] mb-2 block">Select Date</Label>
            <Input
              data-testid="date-filter-input"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-64"
            />
          </div>
          <div className="pt-6">
            <Card className="px-6 py-3 bg-[#E2E8E4] border-none">
              <p className="text-sm text-[#1C1C1A]/70">Total Batches: <span className="font-semibold text-[#2C4C3B] text-lg">{batches.length}</span></p>
            </Card>
          </div>
        </div>

        {batches.length === 0 ? (
          <Card className="p-12 bg-white border border-[#E5E5DF] rounded-lg text-center">
            <div className="max-w-md mx-auto">
              <CalendarBlank size={64} className="mx-auto text-[#2C4C3B]/30 mb-4" />
              <h3 className="text-xl font-semibold text-[#2C4C3B] mb-2">No batches scheduled</h3>
              <p className="text-[#1C1C1A]/60 mb-6">Click &apos;Generate 16 Batches&apos; to automatically create the daily schedule</p>
              <Button
                onClick={handleGenerateBatches}
                disabled={generating}
                className="bg-[#C05C42] hover:bg-[#C05C42]/90 text-white rounded-md"
              >
                <Lightning size={20} weight="fill" className="mr-2" />
                Generate 16 Batches Now
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedBatches).map(([timeOfDay, timeBatches]) => (
              timeBatches.length > 0 && (
                <div key={timeOfDay}>
                  <h2 className="text-xl font-semibold text-[#2C4C3B] mb-4 flex items-center gap-2">
                    <Clock size={24} weight="duotone" />
                    {timeOfDay} ({timeBatches.length} batches)
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="batches-grid">
                    {timeBatches.map((batch, index) => {
                      const occupancyPercent = (batch.assigned_customers.length / batch.max_capacity) * 100;
                      const isFull = occupancyPercent === 100;
                      const isAlmostFull = occupancyPercent >= 80;
                      
                      return (
                        <Card
                          key={batch.batch_id}
                          className={`p-5 bg-white border rounded-lg hover:-translate-y-1 transition-transform ${
                            isFull ? 'border-red-300 bg-red-50' : isAlmostFull ? 'border-yellow-300 bg-yellow-50' : 'border-[#E5E5DF]'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="p-2 rounded-lg bg-[#2C4C3B]/10">
                                <Clock size={20} weight="duotone" className="text-[#2C4C3B]" />
                              </div>
                              <div>
                                <p className="font-bold text-lg text-[#2C4C3B]">{batch.start_time}</p>
                                <p className="text-xs text-[#1C1C1A]/60">to {batch.end_time}</p>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              isFull ? 'bg-red-200 text-red-800' : isAlmostFull ? 'bg-yellow-200 text-yellow-800' : 'bg-[#E2E8E4] text-[#2C4C3B]'
                            }`}>
                              {batch.assigned_customers.length}/{batch.max_capacity}
                            </span>
                          </div>

                          <div className="w-full bg-[#E2E8E4] rounded-full h-2.5 mb-3">
                            <div
                              className={`h-2.5 rounded-full transition-all ${
                                isFull ? 'bg-red-600' : isAlmostFull ? 'bg-yellow-600' : 'bg-[#2C4C3B]'
                              }`}
                              style={{ width: `${occupancyPercent}%` }}
                            ></div>
                          </div>

                          <div className="flex items-center justify-between text-xs">
                            <span className="text-[#1C1C1A]/60">Available</span>
                            <span className={`font-bold ${
                              isFull ? 'text-red-600' : isAlmostFull ? 'text-yellow-700' : 'text-[#2C4C3B]'
                            }`}>
                              {batch.max_capacity - batch.assigned_customers.length} beds
                            </span>
                          </div>
                          
                          {isFull && (
                            <div className="mt-2 text-xs text-red-600 font-semibold text-center">
                              FULL
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {batches.length > 0 && (
          <Card className="mt-8 p-6 bg-[#E2E8E4] border-none">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-1">Total Capacity</p>
                <p className="text-2xl font-bold text-[#2C4C3B]">{batches.length * 15} slots</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-1">Occupied</p>
                <p className="text-2xl font-bold text-[#2C4C3B]">
                  {batches.reduce((sum, b) => sum + b.assigned_customers.length, 0)} slots
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-1">Available</p>
                <p className="text-2xl font-bold text-[#2C4C3B]">
                  {batches.reduce((sum, b) => sum + (b.max_capacity - b.assigned_customers.length), 0)} slots
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-1">Utilization</p>
                <p className="text-2xl font-bold text-[#C05C42]">
                  {Math.round((batches.reduce((sum, b) => sum + b.assigned_customers.length, 0) / (batches.length * 15)) * 100)}%
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Batches;